// ./src/utils/api/PaymentService.ts
//
// The money ledger (transactions) — read-only from the app. Every sale + refund lands here as a
// `payment` row (append-only; refunds are their own rows linked by refundOfPaymentId). The Box Office
// writes them; this service only lists/reads them for the Transactions page. Account-scoped server-side.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

/** Ledger direction. */
export const PAYMENT_DIRECTION = {
  PLATFORM_REVENUE: "platform_revenue",
  HOST_REVENUE: "host_revenue",
} as const;

/** Settlement status of a payment row. */
export const PAYMENT_STATUS = {
  PENDING: "pending",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  REFUNDED: "refunded",
  PARTIALLY_REFUNDED: "partially_refunded",
  CANCELED: "canceled",
} as const;

/** What was paid for. */
export const PAYMENT_TYPE = {
  TICKET: "ticket",
  ADDON: "addon",
  DONATION: "donation",
  MEMBERSHIP: "membership",
  SPONSORSHIP: "sponsorship",
  /** #149 — a guest paid for sign-up list items online (subject: signup_order). */
  SIGNUP: "signup",
  EVENT_UPGRADE: "event_upgrade",
  OTHER: "other",
} as const;

export interface Payment {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  paymentType?: string | null;
  isTest?: boolean | null;
  isRefund?: boolean | null;
  refundOfPaymentId?: string | null;
  direction?: string | null;
  subjectKind?: string | null;
  subjectId?: string | null;
  currencyCode?: string | null;
  grossAmount?: number | null;
  serviceFeeAmount?: number | null;
  /**
   * Does this account's PLAN return our service fee when a charge is refunded?
   * ⚠ THE PLAN HALF ONLY — the server also requires the refunds to reach the WHOLE charge. Do not
   * combine the two here: that is `platformFeeComesBack`, and it lives on the server.
   */
  serviceFeeRefundable?: boolean | null;
  processorFeeAmount?: number | null;
  /**
   * #201 — where `processorFeeAmount` came from: null = the checkout estimate (not read back from Stripe yet), 'stripe' =
   * what Stripe really took, 'unavailable' = Stripe could not say, so the estimate stays. "est." unless 'stripe'.
   */
  processorFeeSource?: string | null;
  /** #201 — the checkout estimate, kept once the real fee replaced it (null when none was made). */
  processorFeeEstimateAmount?: number | null;
  netAmount?: number | null;
  refundedAmount?: number | null;
  refundedAt?: string | null;
  processor?: string | null;
  processorPaymentId?: string | null;
  processorAccountId?: string | null;
  /** The sponsorship application this settles, when money arrived before approval. */
  partnerApplicationId?: string | null;
  paymentStatus?: string | null;
  payoutStatus?: string | null;
  /** Short human reference (e.g. AB7K9Q) — on the receipt + the guest's bank statement (R20). */
  referenceCode?: string | null;
  personId?: string | null;
  payerFirstName?: string | null;
  payerLastName?: string | null;
  payerEmail?: string | null;
  payerPhone?: string | null;
  /** A2e #87 — the billing address as envelope lines, formatted by the server ([] when none). */
  payerAddressLines?: string[] | null;
  /** The billing address's region and country as entered (a two-letter code when picked from autocomplete). */
  payerState?: string | null;
  payerCountry?: string | null;
  paidAt?: string | null;
  note?: string | null;
  /** Snapshot of the order price breakdown (tax/subtotal/discount aren't columns). */
  metadata?: { pricing?: PaymentPricingSnapshot } | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** The pricing snapshot stored in payment.metadata (R14) — mirrors the order's settings.pricing. */
export interface PaymentPricingSnapshot {
  subtotal?: number | null;
  discount?: number | null;
  tax?: number | null;
  serviceFee?: number | null;
  processorFee?: number | null;
  /** The fee amounts actually PASSED TO the guest (0 under ABSORB) — what the buyer's receipt shows. */
  serviceFeeToGuest?: number | null;
  processorFeeToGuest?: number | null;
  feeToGuest?: number | null;
  total?: number | null;
  hostNet?: number | null;
  feeMode?: string | null;
  currencyCode?: string | null;
  isTest?: boolean | null;
}

export interface PaymentFilter {
  id?: string[];
  eventId?: string[];
  personId?: string[];
  paymentType?: string[];
  isTest?: boolean | null;
  /** true = refunds only, false = sales only, omitted = both. */
  isRefund?: boolean | null;
  direction?: string[];
  paymentStatus?: string[];
  /** Exact transaction reference code(s). */
  referenceCode?: string[];
  /** Transaction date range (ISO). */
  paidAtFrom?: string | null;
  paidAtTo?: string | null;
  searchKeyword?: string;
  /** ⚠ One sponsorship application's money — the charge and any refund of it. */
  partnerApplicationId?: string[];
}

const PAYMENT_FIELDS = `
  id
  eventId
  paymentType
  isTest
  isRefund
  refundOfPaymentId
  direction
  subjectKind
  subjectId
  currencyCode
  grossAmount
  serviceFeeAmount
  serviceFeeRefundable
  processorFeeAmount
  processorFeeSource
  processorFeeEstimateAmount
  netAmount
  refundedAmount
  refundedAt
  processor
  processorPaymentId
  processorAccountId
  partnerApplicationId
  paymentStatus
  payoutStatus
  referenceCode
  personId
  payerFirstName
  payerLastName
  payerEmail
  payerPhone
  payerAddressLines
  payerState
  payerCountry
  paidAt
  note
  metadata
  status
  createdAt
`;

const PAYMENT_LIST_QUERY = gql`
  query PaymentList($filter: PaymentFilter, $params: PaginationAndSorting) {
    paymentList(filter: $filter, params: $params) {
      code
      total
      errors { name errors }
      data { ${PAYMENT_FIELDS} }
    }
  }
`;

const PAYMENT_GET_QUERY = gql`
  query PaymentGet($id: ID) {
    paymentGet(id: $id) {
      code
      errors { name errors }
      data { ${PAYMENT_FIELDS} }
    }
  }
`;

const PAYMENT_SEND_RECEIPT_MUTATION = gql`
  mutation PaymentSendReceipt($params: PaymentSendReceiptInput) {
    paymentSendReceipt(params: $params) {
      code
      errors { name errors }
      data { ${PAYMENT_FIELDS} }
    }
  }
`;

export class PaymentService {
  static async list(filter?: PaymentFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(PAYMENT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(PAYMENT_GET_QUERY, { id });
  }

  /** Re-send the buyer's itemized receipt email (R19). `siteOrigin` = the public events-web origin. */
  static async sendReceipt(id: string, siteOrigin?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(PAYMENT_SEND_RECEIPT_MUTATION, { params: { id, siteOrigin: siteOrigin || null } });
  }

  /**
   * Page through the WHOLE filtered set (for CSV export), through `listAllPages`.
   * ⚠ ROADMAP #118: this used to return a bare array — a failed page ended the loop and the rows read before it went
   * into the CSV as if complete, and it stopped after 101 pages (20,200 rows) without a word. A failure now fails it.
   */
  static async listAll(filter?: PaymentFilter, sortBy?: SortItem[]): Promise<OpResult> {
    return listAllPages<Payment>(PaymentService.list, filter, sortBy);
  }
}
